import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { Row, Col, Button } from 'reactstrap';
import moment from 'moment';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    cancelGeneralJournal,
    createGeneralJournal,
    getGeneralJournalDetails,
    getGeneralJournalNumber,
    getLedgerAccounts,
    updateGeneralJournal,
} from '../../services/GeneralJournalServices';
import { AccountingHintPanel } from './VoucherUtils';
import { getCurrentDate } from '../../utils/DateUtils';
import { fetchAndShowGeneralJournalPdf } from '../../utils/generalJournalPdf';
import {
    buildJournalLinePayload,
    buildLedgerSelectOptions,
    emptyJournalLine,
    resolveAccountLabel,
    sumCredits,
    sumDebits,
    validateJournalLines,
} from './GeneralJournalUtils';

function displayDateToInput(value) {
    if (!value) {
        return '';
    }
    const parts = String(value).split('-');
    if (parts.length === 3 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return value;
}

function GeneralJournal() {
    const { id: editId } = useParams();
    const isEditMode = Boolean(editId);
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [recordLoaded, setRecordLoaded] = useState(!isEditMode);
    const [inputs, setInputs] = useState({
        journal_date: getCurrentDate(),
        remarks: '',
    });
    const [plants, setPlants] = useState([]);
    const [ledgerAccounts, setLedgerAccounts] = useState([]);
    const [journalLines, setJournalLines] = useState([emptyJournalLine(), emptyJournalLine()]);
    const [accountingHint, setAccountingHint] = useState(null);
    const [inProgress, setInProgress] = useState(false);

    const totalDebit = useMemo(() => sumDebits(journalLines), [journalLines]);
    const totalCredit = useMemo(() => sumCredits(journalLines), [journalLines]);
    const balanceDiff = useMemo(
        () => Math.round((totalDebit - totalCredit) * 100) / 100,
        [totalDebit, totalCredit],
    );

    const ledgerOptions = useMemo(
        () => buildLedgerSelectOptions(ledgerAccounts),
        [ledgerAccounts],
    );

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
        });
        getLedgerAccounts(cookies).then((response) => {
            setLedgerAccounts(response.ledger_accounts || []);
            setAccountingHint(response.accounting_hint || null);
        }).catch((error) => {
            displayError(error?.response?.data || error, 'Ledger accounts');
        });
    }, [cookies]);

    useEffect(() => {
        if (!ledgerAccounts.length) {
            return;
        }
        setJournalLines((lines) => lines.map((line) => {
            if (!line.account_id || line.account_label) {
                return line;
            }
            return {
                ...line,
                account_label: resolveAccountLabel(ledgerAccounts, line.account_id, line.account_label),
            };
        }));
    }, [ledgerAccounts]);

    useEffect(() => {
        if (!isEditMode || !editId) {
            return;
        }
        setInProgress(true);
        getGeneralJournalDetails(cookies, editId)
            .then((record) => {
                if (!record.status) {
                    navigate(`/GeneralJournalEdit/${editId}`);
                    return;
                }
                setInputs({
                    plant_id: record.plant_id,
                    prefix: record.prefix,
                    sl_no: record.sl_no,
                    journal_no: record.journal_no,
                    journal_date: displayDateToInput(record.journal_date),
                    remarks: record.remarks || '',
                });
                setAccountingHint(record.accounting_hint || null);
                setJournalLines((record.journal_lines || []).map((line) => ({
                    id: `line-${line.id}`,
                    account_id: line.account_id,
                    account_label: line.account_name,
                    debit_amt: line.debit_amt ? String(line.debit_amt) : '',
                    credit_amt: line.credit_amt ? String(line.credit_amt) : '',
                    line_narration: line.line_narration || '',
                })));
            })
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => {
                setRecordLoaded(true);
                setInProgress(false);
            });
    }, [cookies, editId, isEditMode, navigate]);

    useEffect(() => {
        if (isEditMode || !inputs.plant_id || !inputs.journal_date) {
            return;
        }
        getGeneralJournalNumber(
            cookies,
            inputs.plant_id,
            moment(inputs.journal_date).format('DD-MM-YYYY'),
        ).then((response) => {
            setInputs((values) => ({
                ...values,
                prefix: response.prefix,
                sl_no: response.journal_no,
                journal_no: `${response.prefix}/${response.journal_no}`,
            }));
        }).catch((error) => {
            displayError(error?.response?.data || error, 'Journal number');
        });
    }, [cookies, inputs.plant_id, inputs.journal_date, isEditMode]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((values) => ({ ...values, [name]: value }));
    };

    const handleLineChange = (lineId, field, value) => {
        setJournalLines((lines) => lines.map((line) => {
            if (line.id !== lineId) {
                return line;
            }
            const next = { ...line, [field]: value };
            if (field === 'debit_amt' && value) {
                next.credit_amt = '';
            }
            if (field === 'credit_amt' && value) {
                next.debit_amt = '';
            }
            return next;
        }));
    };

    const handleAccountSelect = (lineId, option) => {
        setJournalLines((lines) => lines.map((line) => (
            line.id === lineId
                ? {
                    ...line,
                    account_id: option ? option.value : '',
                    account_label: option ? option.label : '',
                }
                : line
        )));
    };

    const addLine = () => {
        setJournalLines((lines) => [...lines, emptyJournalLine()]);
    };

    const removeLine = (lineId) => {
        setJournalLines((lines) => {
            if (lines.length <= 2) {
                Swal.fire('Validation', 'At least two journal lines are required.', 'warning');
                return lines;
            }
            return lines.filter((line) => line.id !== lineId);
        });
    };

    const handlePrint = () => {
        if (!editId) {
            return;
        }
        setInProgress(true);
        fetchAndShowGeneralJournalPdf(cookies, editId, { title: 'General Journal' })
            .finally(() => setInProgress(false));
    };

    const handleCancelVoucher = () => {
        if (!editId) {
            return;
        }
        Swal.fire({
            title: 'Cancel general journal?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            cancelGeneralJournal(cookies, editId)
                .then((response) => {
                    Swal.fire('Cancelled', response.message || 'General journal cancelled.', 'success');
                    navigate(`/GeneralJournalEdit/${editId}`);
                })
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!inputs.plant_id) {
            Swal.fire('Validation', 'Plant is required.', 'warning');
            return;
        }
        const validationError = validateJournalLines(journalLines);
        if (validationError) {
            Swal.fire('Validation', validationError, 'warning');
            return;
        }
        setInProgress(true);
        const payload = {
            plant_id: inputs.plant_id,
            prefix: inputs.prefix,
            sl_no: inputs.sl_no,
            journal_date: moment(inputs.journal_date).format('DD-MM-YYYY'),
            remarks: inputs.remarks || '',
            journal_lines: buildJournalLinePayload(journalLines),
        };
        const savePromise = isEditMode
            ? updateGeneralJournal(cookies, editId, payload)
            : createGeneralJournal(cookies, payload);
        savePromise
            .then((response) => {
                Swal.fire('Saved', response.message || 'General journal saved.', 'success');
                if (isEditMode) {
                    navigate(`/GeneralJournalEdit/${response.id || editId}`);
                } else {
                    navigate(`/GeneralJournalEdit/${response.id}`);
                }
            })
            .catch((error) => displayError(error?.response?.data || error, 'Save Failed'))
            .finally(() => setInProgress(false));
    };

    if (isEditMode && !recordLoaded) {
        return (
            <div id="outer-container" className="App">
                <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
                <div id="page-wrap">
                    <LoadingOverlay active />
                </div>
            </div>
        );
    }

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <FormPageLayout
                    moduleLabel="ACCOUNTS"
                    title="General Journal"
                    subtitle={inputs.journal_no || 'Balanced debit and credit entries'}
                    modeLabel={isEditMode ? 'EDIT MODE' : 'CREATE MODE'}
                >
                    <ErrorToast />
                    <AccountingHintPanel hint={accountingHint} />
                    <form onSubmit={handleSubmit}>
                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel align-items-end">
                            <Col sm={4} md={4}>
                                <label htmlFor="plant_id" className="mb-1 d-block font-weight-bold">Plant *</label>
                                <select
                                    id="plant_id"
                                    name="plant_id"
                                    className="form-control mandatory-form-control"
                                    value={inputs.plant_id || ''}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select plant</option>
                                    {plants.map((plant) => (
                                        <option key={plant.value} value={plant.value}>{plant.label}</option>
                                    ))}
                                </select>
                            </Col>
                            <Col sm={4} md={4}>
                                <label htmlFor="journal_no" className="mb-1 d-block font-weight-bold">Journal No</label>
                                <input
                                    id="journal_no"
                                    name="journal_no"
                                    className="form-control"
                                    value={inputs.journal_no || ''}
                                    readOnly
                                />
                            </Col>
                            <Col sm={4} md={4}>
                                <label htmlFor="journal_date" className="mb-1 d-block font-weight-bold">Journal Date *</label>
                                <input
                                    id="journal_date"
                                    name="journal_date"
                                    type="date"
                                    className="form-control mandatory-form-control"
                                    value={inputs.journal_date || ''}
                                    onChange={handleChange}
                                    required
                                />
                            </Col>
                        </Row>

                        <div className="form-row table-bordered shadow p-3 my-2 border-secondary form-control-panel table-responsive">
                            <table className="table table-sm table-bordered mb-2">
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: 260 }}>Account</th>
                                        <th style={{ width: 140 }}>Debit</th>
                                        <th style={{ width: 140 }}>Credit</th>
                                        <th style={{ minWidth: 200 }}>Line Narration</th>
                                        <th style={{ width: 70 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {journalLines.map((line, index) => (
                                        <tr key={line.id}>
                                            <td>
                                                <Select
                                                    options={ledgerOptions}
                                                    value={line.account_id
                                                        ? { value: line.account_id, label: line.account_label }
                                                        : null}
                                                    onChange={(option) => handleAccountSelect(line.id, option)}
                                                    placeholder={`Select ledger (line ${index + 1})`}
                                                    classNamePrefix="react-select"
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    className="form-control"
                                                    value={line.debit_amt}
                                                    onChange={(e) => handleLineChange(line.id, 'debit_amt', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    className="form-control"
                                                    value={line.credit_amt}
                                                    onChange={(e) => handleLineChange(line.id, 'credit_amt', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={line.line_narration}
                                                    onChange={(e) => handleLineChange(line.id, 'line_narration', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    type="button"
                                                    onClick={() => removeLine(line.id)}
                                                >
                                                    X
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th className="text-right">Totals</th>
                                        <th>{totalDebit.toFixed(2)}</th>
                                        <th>{totalCredit.toFixed(2)}</th>
                                        <th colSpan={2}>
                                            {balanceDiff === 0
                                                ? <span className="text-success">Balanced</span>
                                                : <span className="text-danger">{`Difference: ${balanceDiff.toFixed(2)}`}</span>}
                                        </th>
                                    </tr>
                                </tfoot>
                            </table>
                            <Button color="info" type="button" size="sm" onClick={addLine}>Add Line</Button>
                        </div>

                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col sm={12}>
                                <label htmlFor="remarks" className="mb-1 d-block font-weight-bold">Narration / Remarks</label>
                                <textarea
                                    id="remarks"
                                    name="remarks"
                                    className="form-control"
                                    rows={2}
                                    value={inputs.remarks || ''}
                                    onChange={handleChange}
                                    placeholder="Enter overall journal narration or remarks"
                                />
                            </Col>
                        </Row>

                        <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                            <Col className="d-flex justify-content-end flex-wrap">
                                {isEditMode && (
                                    <>
                                        <Button
                                            color="success"
                                            type="button"
                                            className="mr-2 mb-2"
                                            onClick={handlePrint}
                                            disabled={!recordLoaded}
                                        >
                                            Print
                                        </Button>
                                        <Button
                                            color="warning"
                                            type="button"
                                            className="mr-2 mb-2"
                                            onClick={handleCancelVoucher}
                                            disabled={!recordLoaded || inProgress}
                                        >
                                            Cancel Voucher
                                        </Button>
                                    </>
                                )}
                                <Button color="primary" type="submit" className="mr-2 mb-2" disabled={inProgress || (isEditMode && !recordLoaded)}>
                                    {isEditMode ? 'Update' : 'Save'}
                                </Button>
                                <Button
                                    color="secondary"
                                    type="button"
                                    className="mb-2"
                                    onClick={() => navigate('/GeneralJournalTable')}
                                >
                                    Back to List
                                </Button>
                            </Col>
                        </Row>

                        <FloatingControls
                            tableLink="/GeneralJournalTable"
                            onCancel={() => navigate('/GeneralJournalTable')}
                            enableCancel
                        />
                    </form>
                </FormPageLayout>
                <LoadingOverlay active={inProgress} />
            </div>
        </div>
    );
}

export default GeneralJournal;
